import React, { useState } from 'react';
import getValue from 'lodash.get';
import Typography from '@mui/material/Typography';
import idScan from './assets/id-scan.json';
import SectionContainer from '../layout/SectionContainer.jsx';
import SectionNav from '../layout/SectionNav.jsx';
import WelcomeScreen from '../layout/WelcomeScreen.jsx';
import { useIdDocumentCapture } from '../hooks/useIdDocumentCapture.jsx';
import { getFlagMessages } from './translations/documentFlagMessages.js';
import { getTextDefaults } from './translations/IDCardSectionTranslation.js';

// Guided ID types the agentic parser supports. Deliberately excludes `driving` (still on the legacy
// blink-id parser) — SimpleForm.js keeps any driving-carrying config on the generic DocumentSection.
const GUIDED_ID_TYPES = ['id', 'passport', 'residence-permit'];

const WelcomePage = ({ text }) => (
	<WelcomeScreen animation={idScan}>
		<Typography variant="h6">{text.welcomeTitle}</Typography>
		<Typography>{text.welcomeReason}</Typography>
	</WelcomeScreen>
);

// Flatten SimpleForm's OR-group (e.g. [['id','passport','residence-permit']]) into the accepted-type
// list the picker shows. any-id → id/passport for any legacy carriers; driving is dropped (see above).
const toAcceptedTypes = (docs = []) => {
	const flat = docs
		.flatMap((d) => (Array.isArray(d) ? d : [d]))
		.flatMap((t) => (t === 'any-id' ? ['id', 'passport'] : [t]));
	return [...new Set(flat.filter((t) => GUIDED_ID_TYPES.includes(t)))];
};

const GuidedIdSection = ({
	acceptedDocuments = [],
	possibleDocuments,
	requiredDocuments,
	onChange = () => {},
	values = {},
	name = 'userDocuments',
	textOverride = {},
	uploadUrl = '/api/files',
	background = 'light',
	selectedLanguage = 'EN',
	noWelcome = false,
	addPendingAction = () => {},
	removePendingAction = () => {},
	useCamera = true,
	cameraIncludes = '',
	cameraRotationAngle = 0,
	disabled = false,
	navigationProps,
	style = {},
}) => {
	const text = Object.assign({}, getTextDefaults(selectedLanguage), textOverride);
	const value = getValue(values, name) || [];
	// Accept either an explicit acceptedDocuments prop or the SimpleForm possible/required lists.
	const acceptedTypes = toAcceptedTypes(
		acceptedDocuments.length ? acceptedDocuments : requiredDocuments || possibleDocuments || [],
	);
	const [screen, setScreen] = useState(noWelcome ? 'capture' : 'welcome');

	let uploadContext;
	if (values?.userData?.firstName || values?.userData?.lastName) {
		uploadContext = { userData: { firstName: values.userData.firstName, lastName: values.userData.lastName } };
	}

	const guided = useIdDocumentCapture({
		acceptedTypes,
		document: value[0],
		onDocument: (file) => onChange({ target: { name, value: [file] } }),
		text,
		uploadUrl,
		context: uploadContext,
		reviewMessages: getFlagMessages(selectedLanguage),
		useCamera,
		forceCamera: typeof window !== 'undefined' && window.location.href.includes('deviceId='),
		cameraIncludes,
		cameraRotationAngle,
		disabled,
		addPendingAction,
		removePendingAction,
	});

	const handleNext = (event) => {
		if (screen === 'welcome') return setScreen('capture');
		if (guided.subScreen === 'type') return; // card tap advances; Next is inert on the picker
		return navigationProps.next(event);
	};
	const handlePrevious = (event) => {
		if (screen === 'capture' && guided.goBack()) return; // capture → picker
		if (screen === 'capture' && !noWelcome) return setScreen('welcome');
		return navigationProps.previous(event);
	};

	const canContinue = screen === 'welcome' ? true : guided.canContinue;
	const hasPrevious =
		navigationProps.hasPrevious || (screen === 'capture' && (guided.subScreen === 'capture' || !noWelcome));

	return (
		<SectionContainer
			background={background}
			style={style}
			Navigation={
				<SectionNav
					{...navigationProps}
					hasPrevious={hasPrevious}
					canContinue={canContinue}
					hasNext={true}
					next={handleNext}
					selectedLanguage={selectedLanguage}
					previous={handlePrevious}
				/>
			}
		>
			{screen === 'welcome' ? <WelcomePage text={text} /> : guided.node}
		</SectionContainer>
	);
};

GuidedIdSection.type = 'GuidedIdSection';
GuidedIdSection.hasNavigation = true;

export default GuidedIdSection;
